import React, { useState, useEffect } from 'react';
import { ChevronDown, Calendar, BookOpen, Search } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend, ComposedChart, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import api from '../../services/api';
import Toast from '../../components/Toast';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
const MONTHS = [
  { value: '', label: 'All Months' },
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' },
  { value: 5, label: 'May' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dec' },
];


export default function Reports() {
  const [stats, setStats] = useState({ total_students: 0, total_lecturers: 0, total_exams: 0, total_grades: 0 });
  const [loading, setLoading] = useState(true);
  const [gradeDistribution, setGradeDistribution] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [attendanceGranularity, setAttendanceGranularity] = useState('month');
  const [topStudents, setTopStudents] = useState([]);
  const [leastStudents, setLeastStudents] = useState([]);
  const [recentStudents, setRecentStudents] = useState([]);
  const [examAttendance, setExamAttendance] = useState([]);
  // Students + search
  const [students, setStudents] = useState([]);
  const [studentQuery, setStudentQuery] = useState('');
  const [studentSuggestions, setStudentSuggestions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportSheets, setExportSheets] = useState('combined');

  // Download states and progress
  const [gradeDownloading, setGradeDownloading] = useState(false);
  const [studentDownloading, setStudentDownloading] = useState(false);
  const [allDownloading, setAllDownloading] = useState(false);
  const [gradeDownloadProgress, setGradeDownloadProgress] = useState(0);
  const [studentDownloadProgress, setStudentDownloadProgress] = useState(0);
  const [allDownloadProgress, setAllDownloadProgress] = useState(0);

  // Toast queue (stacking)
  const [toasts, setToasts] = useState([]);
  const pushToast = (message, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  };
  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  // Placeholder for exams/subjects data
  const [examsData, setExamsData] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);

  // Additional analytics
  const [subjectAvg, setSubjectAvg] = useState([]);
  const [subjectAttendance, setSubjectAttendance] = useState([]);
  const [examParticipation, setExamParticipation] = useState([]);
  const [topSubjects, setTopSubjects] = useState([]);

  // Student specific analysis
  const [studentAnalysis, setStudentAnalysis] = useState({ marks: [], attendance: [] });
  const [showStudentPanel, setShowStudentPanel] = useState(false);
  const [hoveredAttendanceIndex, setHoveredAttendanceIndex] = useState(null);
  // Student attendance range: 1m (daily), 6m/1y/2y (monthly aggregation)
  const [studentAttendanceRange, setStudentAttendanceRange] = useState('1m');

  // Helper: friendly date formatting
  const formatDate = (d, monthly = false) => {
    if (!d) return '';
    const date = new Date(d);
    if (monthly) return date.toLocaleString(undefined, { month: 'short', year: 'numeric' });
    return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // Prepare chart data for the student's attendance depending on selected range
  const getStudentAttendanceChartData = (range) => {
    const raw = (studentAnalysis.attendance || []).map(a => ({ ...a, _date: a.session_date ? new Date(a.session_date) : (a.date ? new Date(a.date) : null) })).filter(a => a._date);
    if (raw.length === 0) return { data: [], xKey: 'session_date', aggregated: false };

    const now = new Date();
    const start = new Date(now);
    if (range === '1m') start.setMonth(start.getMonth() - 1);
    else if (range === '6m') start.setMonth(start.getMonth() - 6);
    else if (range === '1y') start.setFullYear(start.getFullYear() - 1);
    else if (range === '2y') start.setFullYear(start.getFullYear() - 2);

    const filtered = raw.filter(r => r._date >= start).sort((a,b) => a._date - b._date);

    if (range === '1m') {
      // per-day entries
      const data = filtered.map(f => ({
        session_date: formatDate(f._date, false),
        present: f.status === 'Present' ? 100 : 0,
        absent: f.status === 'Absent' ? 100 : 0
      }));
      return { data, xKey: 'session_date', aggregated: false };
    }

    // aggregate per month
    const buckets = new Map();
    filtered.forEach(f => {
      const key = `${f._date.getFullYear()}-${String(f._date.getMonth()+1).padStart(2,'0')}`;
      if (!buckets.has(key)) buckets.set(key, { year: f._date.getFullYear(), month: f._date.getMonth(), present: 0, absent: 0, total: 0 });
      const b = buckets.get(key);
      if (f.status === 'Present') b.present++;
      else b.absent++;
      b.total++;
    });

    const data = Array.from(buckets.entries()).sort().map(([k, v]) => ({
      month: new Date(v.year, v.month, 1).toISOString(),
      monthLabel: formatDate(new Date(v.year, v.month, 1), true),
      present: v.total > 0 ? Math.round((v.present / v.total) * 100) : 0,
      absent: v.total > 0 ? Math.round((v.absent / v.total) * 100) : 0
    }));

    return { data, xKey: 'monthLabel', aggregated: true };
  };

  useEffect(() => {
    loadGrades();
    loadStudents();
  }, []);

  useEffect(() => {
    loadDashboardData();
    // TODO: loadExamsData(); loadSubjectsData();
  }, [selectedGrade, selectedMonth, attendanceGranularity, selectedStudent]);

  const loadStudents = async () => {
    try {
      const res = await api.get('/admin/students');
      if (res.data.success && Array.isArray(res.data.students)) {
        setStudents(res.data.students);
        // recent students (by enrollment_date or created_at)
        const recent = (res.data.students || []).slice().sort((a,b) => new Date(b.enrollment_date || b.created_at || 0) - new Date(a.enrollment_date || a.created_at || 0)).slice(0, 10);
        setRecentStudents(recent);
      }
    } catch (err) {
    }
  };

  useEffect(() => {
    if (!studentQuery) {
      setStudentSuggestions([]);
      return;
    }
    // If we already have a selected student and the query equals their name, hide suggestions
    if (selectedStudent && studentQuery === selectedStudent.full_name) {
      setStudentSuggestions([]);
      return;
    }
    const q = studentQuery.toLowerCase();
    const matches = students.filter(s => (s.full_name || '').toLowerCase().includes(q)).slice(0, 8);
    setStudentSuggestions(matches);
  }, [studentQuery, students, selectedStudent]);

  // Animate student panel on selection
  useEffect(() => {
    if (selectedStudent) {
      // start hidden then reveal on next frame to trigger transition
      setShowStudentPanel(false);
      requestAnimationFrame(() => setShowStudentPanel(true));
    } else {
      setShowStudentPanel(false);
    }
  }, [selectedStudent]);

  const loadGrades = async () => {
    try {
      const res = await api.get('/admin/grades');
      if (res.data.success) setGrades(res.data.grades || []);
    } catch (err) {
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedGrade) params.grade_id = selectedGrade;
      if (selectedMonth) params.month = selectedMonth;
      if (selectedStudent) params.student_id = selectedStudent.user_id;
      if (attendanceGranularity) params.attendance_granularity = attendanceGranularity;
      const dashRes = await api.get('/admin/dashboard', { params });
      if (dashRes.data.success && dashRes.data.dashboard) {
        const dash = dashRes.data.dashboard;
        setStats(dash);
        // Set real grade distribution data
        if (Array.isArray(dash.grade_distribution)) {
          setGradeDistribution(dash.grade_distribution.map((g, i) => ({
            name: g.grade_name || 'Unknown',
            value: g.student_count || 0,
            color: COLORS[i % COLORS.length]
          })));
        }
        // Set real attendance trend data
        setAttendanceData(Array.isArray(dash.attendance_trend) ? dash.attendance_trend : []);
        setTopStudents(Array.isArray(dash.top_students) ? dash.top_students : []);
        setLeastStudents(Array.isArray(dash.least_students) ? dash.least_students : []);
        setExamAttendance(Array.isArray(dash.exam_attendance) ? dash.exam_attendance : []);

        // New analytics
        setSubjectAvg(Array.isArray(dash.subject_avg) ? dash.subject_avg : []);
        setSubjectAttendance(Array.isArray(dash.subject_attendance) ? dash.subject_attendance : []);
        setExamParticipation(Array.isArray(dash.exam_participation) ? dash.exam_participation : []);
        setTopSubjects(Array.isArray(dash.top_subjects) ? dash.top_subjects : []);

        // Student-specific analysis (transform attendance to present/absent percentage fields)
        const sAnal = dash.student_analysis || { marks: [], attendance: [] };
        if (Array.isArray(sAnal.attendance)) {
          sAnal.attendance = sAnal.attendance.map(a => ({
            ...a,
            // use percentages so the chart domain matches the Attendance Trend style
            present: a.status === 'Present' ? 100 : 0,
            absent: a.status === 'Absent' ? 100 : 0,
            // normalize possible date keys into session_date for consistent display
            session_date: a.session_date || a.date || a.session_label || a.session_start || ''
          }));
        }
        setStudentAnalysis(sAnal);

        // If report was requested for students fetch, we already have students; otherwise, ensure students list is up to date
        if (!students.length) loadStudents();
      }

      // Update suggestions if a student query is present (keeps dropdown in sync after data loads)
      // But don't repopulate suggestions if we've selected the same student (avoids flicker)
      if (studentQuery && !(selectedStudent && studentQuery === selectedStudent.full_name)) {
        const q = studentQuery.toLowerCase();
        const matches = (students || []).filter(s => (s.full_name || '').toLowerCase().includes(q)).slice(0, 8);
        setStudentSuggestions(matches);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Helper to trigger file download from blob
  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // Helper: retry wrapper for transient failures
  const fetchWithRetry = async (fn, retries = 2, backoff = 1000) => {
    let attempt = 0;
    while (true) {
      try {
        return await fn();
      } catch (err) {
        const status = err?.response?.status;
        const transient = !err?.response || [502, 503, 504].includes(status);
        if (!transient || attempt >= retries) throw err;
        attempt++;
        pushToast(`Retrying download (${attempt}/${retries})...`, 'warning');
        await new Promise(r => setTimeout(r, backoff * Math.pow(2, attempt - 1)));
      }
    }
  };

  // Download grade-level report with progress, retry and toasts
  const downloadGradeReport = async () => {
    if (!selectedGrade) return;
    setGradeDownloading(true);
    setGradeDownloadProgress(0);
    try {
      const params = { export: 1, grade_id: selectedGrade, format: exportFormat, sheets: exportSheets };
      const res = await fetchWithRetry(() => api.get('/admin/reports', {
        params,
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          try {
            const total = progressEvent.total || 0;
            if (total > 0) {
              const percent = Math.round((progressEvent.loaded / total) * 100);
              setGradeDownloadProgress(percent);
            }
          } catch (e) {
            // ignore progress errors
          }
        }
      }));

      const contentType = res.headers['content-type'] || '';
      const contentDisp = res.headers['content-disposition'] || '';
      // Derive filename
      // Friendly filename: use selected grade name if available
      const gradeObj = grades.find(g => String(g.grade_id) === String(selectedGrade));
      const gradeName = gradeObj ? gradeObj.grade_name : selectedGrade;
      let filename = 'grade-' + (gradeName ? gradeName.replace(/[^A-Za-z0-9_\- ]/g, '').replace(/\s+/g, '_') : selectedGrade) + '-report';
      if (contentDisp) {
        const m = contentDisp.match(/filename="?([^";]+)"?/i);
        if (m && m[1]) filename = m[1];
      } else if (contentType.includes('zip')) filename += '.zip';
      else if (contentType.includes('msword') || contentType.includes('application/msword')) filename += '.doc';
      else filename += '.csv';

      downloadBlob(res.data, filename);
      pushToast('Grade report downloaded', 'success');
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to download grade report.';
      pushToast(msg, 'error');
    } finally {
      setGradeDownloading(false);
      setGradeDownloadProgress(0);
    }
  };

  // Download all students (master export)
  const downloadAllExport = async () => {
    setAllDownloading(true);
    setAllDownloadProgress(0);
    try {
      const params = { export: 1, format: exportFormat, sheets: exportSheets };
      const res = await fetchWithRetry(() => api.get('/admin/reports', {
        params,
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          try {
            const total = progressEvent.total || 0;
            if (total > 0) {
              const percent = Math.round((progressEvent.loaded / total) * 100);
              setAllDownloadProgress(percent);
            }
          } catch (e) {}
        }
      }));

      const contentType = res.headers['content-type'] || '';
      const contentDisp = res.headers['content-disposition'] || '';
      let filename = 'all-students-report';
      if (contentDisp) {
        const m = contentDisp.match(/filename="?([^";]+)"?/i);
        if (m && m[1]) filename = m[1];
      } else if (contentType.includes('zip')) filename += '.zip';
      else if (contentType.includes('msword') || contentType.includes('application/msword')) filename += '.doc';
      else filename += '.csv';

      downloadBlob(res.data, filename);
      pushToast('All students export downloaded', 'success');
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to download all students export.';
      pushToast(msg, 'error');
    } finally {
      setAllDownloading(false);
      setAllDownloadProgress(0);
    }
  };

  // Download student-level report with progress, retry and toasts
  const downloadStudentReport = async () => {
    if (!selectedStudent) return;
    setStudentDownloading(true);
    setStudentDownloadProgress(0);
    try {
      const params = { export: 1, student_id: selectedStudent.user_id, format: exportFormat, sheets: exportSheets };
      const res = await fetchWithRetry(() => api.get('/admin/reports', {
        params,
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          try {
            const total = progressEvent.total || 0;
            if (total > 0) {
              const percent = Math.round((progressEvent.loaded / total) * 100);
              setStudentDownloadProgress(percent);
            }
          } catch (e) {
            // ignore
          }
        }
      }));
      const contentType = res.headers['content-type'] || '';
      const contentDisp = res.headers['content-disposition'] || '';
      // Friendly filename: student's full name
      let filename = 'student-' + (selectedStudent.full_name ? selectedStudent.full_name.replace(/[^A-Za-z0-9_\- ]/g, '').replace(/\s+/g, '_') : selectedStudent.user_id) + '-report';
      if (contentDisp) {
        const m = contentDisp.match(/filename="?([^";]+)"?/i);
        if (m && m[1]) filename = m[1];
      } else if (contentType.includes('zip')) filename += '.zip';
      else if (contentType.includes('msword') || contentType.includes('application/msword')) filename += '.doc';
      else filename += '.csv';

      downloadBlob(res.data, filename);
      pushToast('Student report downloaded', 'success');
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to download student report.';
      pushToast(msg, 'error');
      } finally {
      setStudentDownloading(false);
      setStudentDownloadProgress(0);
    }
  };

  // Render abbreviated dots on the attendance chart with hover and offset labels (larger)
  const renderAttendanceDot = (props, key) => {
    const { cx, cy, payload, index } = props || {};
    if (cx == null || cy == null || !payload) return null;
    const value = payload[key];
    // Only render dots for values > 0 (prevents rendering dots for 0 values)
    if (!value || value <= 0) return null;
    const isPresent = key === 'present';
    const color = isPresent ? '#10b981' : '#ef4444';
    const abbrev = isPresent ? 'P' : 'A';
    // choose label offset; present sits a bit lower, absent higher to avoid overlapping
    let offsetY = isPresent ? -12 : -22;
    const fontSize = 12;

    // If point is near the top (value >= 95), render label below the dot instead to avoid clipping
    if (value >= 95) offsetY = 14;

    // If both present and absent have values (aggregated), only show label for the dominant state
    const dominantKey = (payload.present || 0) >= (payload.absent || 0) ? 'present' : 'absent';
    const showLabel = (key === dominantKey) || (payload.absent == null || payload.present == null);

    return (
      <g onMouseEnter={() => setHoveredAttendanceIndex(index)} onMouseLeave={() => setHoveredAttendanceIndex(null)} style={{ cursor: 'pointer' }}>
        <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={1} />
        {showLabel && (
          <text x={cx} y={cy + offsetY} fontSize={fontSize} fill={color} fontWeight={700} textAnchor="middle" stroke="#fff" strokeWidth={3}>{abbrev}</text>
        )}
      </g>
    );
  };

  // Attendance summary counts for the selected student
  const presentCount = (studentAnalysis.attendance || []).filter(a => a.present > 0).length;
  const totalAttendance = (studentAnalysis.attendance || []).length;
  const absentCount = totalAttendance - presentCount;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Reports & Analytics</h1>
          <p className="text-gray-500">System overview and statistics</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="appearance-none pl-4 pr-4 py-2 border border-indigo-200 rounded-[32px] bg-white text-gray-800 text-sm hover:shadow" onClick={() => downloadAllExport()} disabled={allDownloading || gradeDownloading || studentDownloading}>
            {allDownloading ? (
              <div className="flex items-center gap-2"><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 inline-block"></span><span>Exporting...</span></div>
            ) : (
              'Master Export'
            )}
          </button>
        </div>
      </div>

      {/* Toast queue (stacking) */}
      <div className="fixed right-4 top-4 z-50 flex flex-col gap-3">
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} visible={true} onClose={() => removeToast(t.id)} />
        ))}
      </div>

     
      {/* Search & Download row */}
      <div className="mb-4">
        <div className="bg-gradient-to-r from-indigo-200 via-pink-200 to-green-200 p-0.5 rounded-[42px] w-full">
          <div className="bg-white rounded-[40px] flex items-center gap-3 px-4 py-2 w-full relative">
            <div className="flex-1">
              <div className="border border-indigo-200 rounded-md px-3 py-1 relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search student..."
                  className="w-full outline-none text-sm text-gray-500 placeholder-gray-400 pl-8"
                  value={studentQuery}
                  onChange={e => { setStudentQuery(e.target.value); setSelectedStudent(null); }}
                />
              </div>
              {studentSuggestions.length > 0 && studentQuery && (
                <div className="absolute z-50 bg-white border rounded-md mt-1 left-0 right-0 shadow-md max-h-56 overflow-auto">
                  {studentSuggestions.map(s => (
                    <div key={s.user_id} className="px-3 py-2 hover:bg-gray-100 cursor-pointer" onClick={() => { setSelectedStudent(s); setStudentQuery(s.full_name); setStudentSuggestions([]); }}>
                      <div className="text-sm font-medium flex items-center justify-between">
                        <div>{s.full_name}</div>
                        <div className="text-xs text-gray-400 ml-2">{s.grade_name || ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <select className="appearance-none pl-3 pr-8 py-2 border border-indigo-200 rounded-[32px] bg-white text-gray-800 text-sm pr-8" value={exportFormat} onChange={e => setExportFormat(e.target.value)}>
                  <option value="csv">CSV</option>
                  <option value="excel">Excel</option>
                  <option value="word">Word</option>
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><ChevronDown className="w-4 h-4" /></span>
              </div>
              <div className="relative">
                <select className="appearance-none pl-3 pr-8 py-2 border border-indigo-200 rounded-[32px] bg-white text-gray-800 text-sm" value={exportSheets} onChange={e => setExportSheets(e.target.value)}>
                  <option value="combined">Combined</option>
                  <option value="separate">Separate (ZIP)</option>
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><ChevronDown className="w-4 h-4" /></span>
              </div>

              <button className="appearance-none pl-4 pr-4 py-2 border border-indigo-200 rounded-[32px] bg-white text-gray-800 text-sm hover:shadow" onClick={() => downloadStudentReport()} disabled={!selectedStudent || studentDownloading}>
                {studentDownloading ? ( <span className="text-sm">{studentDownloadProgress > 0 ? `Downloading ${studentDownloadProgress}%` : 'Downloading'}</span> ) : ('Export Student')}
              </button>

            </div>
          </div>
        </div>

        {/* Filters below search */}
        <div className="flex gap-4 mt-3">
          <div className="flex flex-col">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Grade</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500"><BookOpen className="w-4 h-4" /></span>
              <select
                className="appearance-none pl-10 pr-8 py-2 border border-indigo-200 rounded-[32px] shadow focus:ring-2 focus:ring-indigo-400 bg-white text-gray-800 font-medium min-w-[140px] transition-all hover:border-indigo-400 focus:border-indigo-500"
                value={selectedGrade}
                onChange={e => setSelectedGrade(e.target.value)}
              >
                <option value="">All Grades</option>
                {grades.map(g => (
                  <option key={g.grade_id} value={g.grade_id}>{g.grade_name}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><ChevronDown className="w-4 h-4" /></span>
            </div>
          </div>

          <div className="flex flex-col">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Month</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-pink-500"><Calendar className="w-4 h-4" /></span>
              <select
                className="appearance-none pl-10 pr-8 py-2 border border-pink-200 rounded-[32px] shadow focus:ring-2 focus:ring-pink-400 bg-white text-gray-800 font-medium min-w-[140px] transition-all hover:border-pink-400 focus:border-pink-500"
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
              >
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><ChevronDown className="w-4 h-4" /></span>
            </div>
          </div>
        </div>
      </div>

      {/* Student-specific analysis (moved above Attendance Trend) */}
      {selectedStudent && (
        <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6 transform transition-all duration-300 ${showStudentPanel ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
          <div className="flex items-start justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Student Analysis: {selectedStudent.full_name}</h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <select className="appearance-none pl-3 pr-8 py-2 border border-indigo-200 rounded-[32px] shadow bg-white text-gray-800 text-sm" value={studentAttendanceRange} onChange={e => setStudentAttendanceRange(e.target.value)}>
                  <option value="1m">1 Month (Daily)</option>
                  <option value="6m">6 Months (Monthly)</option>
                  <option value="1y">1 Year (Monthly)</option>
                  <option value="2y">2 Years (Monthly)</option>
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><ChevronDown className="w-4 h-4" /></span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm text-gray-600 mb-2">Marks Over Time</h4>
              {studentAnalysis.marks.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={studentAnalysis.marks}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="exam_name" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" domain={[0, 100]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="percentage" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-40 flex items-center justify-center text-gray-400">No marks data</div>
              )}
            </div>

            <div>
              <h4 className="text-sm text-gray-600 mb-2">Attendance Log</h4>
              {studentAnalysis.attendance.length > 0 ? (
                <>
                {/* prepare chart data depending on range */}
                {(() => {
                  const chart = getStudentAttendanceChartData(studentAttendanceRange);
                  const chartData = chart.data;
                  const xKey = chart.xKey;
                  const aggregated = chart.aggregated;
                  // compute summary for filtered set
                  const presentCountF = (chartData || []).filter(d => d.present > 0).length;
                  const totalF = (chartData || []).length;
                  const absentCountF = totalF - presentCountF;
                  return (
                    <>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey={xKey} stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" domain={[0, 100]} />
                          <Tooltip formatter={(v) => [`${v}%`, '']} />
                          <Line type="monotone" dataKey="present" name="Present" stroke="#10b981" strokeWidth={2} dot={(p) => renderAttendanceDot(p, 'present')} />
                          <Line type="monotone" dataKey="absent" name="Absent" stroke="#ef4444" strokeWidth={2} dot={(p) => renderAttendanceDot(p, 'absent')} />
                        </LineChart>
                      </ResponsiveContainer>

                      <div className="mt-2 text-sm text-gray-600">
                        <span>Present: <span className="text-green-600 font-semibold">{presentCountF}</span> of <span className="font-medium">{totalF}</span> visible points</span>
                        <span className="ml-4">Absent: <span className="text-red-600 font-semibold">{absentCountF}</span> points</span>
                      </div>
                    </>
                  );
                })()}
                </>
              ) : (
                <div className="h-40 flex items-center justify-center text-gray-400">No attendance data</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Attendance Trend</h3> 
            <div className="flex items-center gap-2">
              <div className="relative">
                <select className="appearance-none pl-3 pr-8 py-2 border border-indigo-200 rounded-[32px] bg-white text-gray-800 text-sm" value={attendanceGranularity} onChange={e => setAttendanceGranularity(e.target.value)}>
                  <option value="day">Last 30 days</option>
                  <option value="month">Last 6 months</option>
                  <option value="year">Last 5 years</option>
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><ChevronDown className="w-4 h-4" /></span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={attendanceData}>
              <defs>
                <linearGradient id="colorAtt" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" domain={[0, 100]} />
              <Tooltip formatter={(v) => [`${v}%`, 'Attendance']} />
              <Area type="monotone" dataKey="attendance" stroke="#6366f1" fill="url(#colorAtt)" strokeWidth={2} />
            </AreaChart>
            
          </ResponsiveContainer>
        </div>

              {/* Grade performance: separate card with one line per grade (duplicate) */}
      {Array.isArray(stats.grade_performance) && stats.grade_performance.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mt-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Grade Performance (All Grades)</h3>
          <ResponsiveContainer width="100%" height={300}>
            {/* Build a common x-axis across all exams */}
            <LineChart data={(() => {
              const grades = stats.grade_performance || [];
              // Collect all exams with dates
              const labelMap = new Map();
              grades.forEach(g => {
                (g.exams || []).forEach(ex => {
                  const key = ex.exam_date || ex.exam_label || ex.exam_name;
                  if (!labelMap.has(key)) {
                    labelMap.set(key, { label: ex.exam_label || ex.exam_name, date: ex.exam_date });
                  }
                });
              });
              // Sort by date
              const labels = Array.from(labelMap.entries()).sort((a,b) => {
                const da = a[1].date || '';
                const db = b[1].date || '';
                return da.localeCompare(db);
              }).map(x => x[0]);

              const data = labels.map(key => ({ label: labelMap.get(key).label }));
              grades.forEach(g => {
                const gradeKey = `grade_${g.grade_id}`;
                (g.exams || []).forEach(ex => {
                  const idx = labels.indexOf(ex.exam_date || ex.exam_label || ex.exam_name);
                  if (idx >= 0) data[idx][gradeKey] = ex.avg_marks;
                });
              });
              return data;
            })()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" domain={[0, 100]} />
              <Tooltip />
              <Legend />
              {/* render lines for each grade */}
              {stats.grade_performance.map((g, i) => (
                <Line key={g.grade_id} type="monotone" dataKey={`grade_${g.grade_id}`} name={g.grade_name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}


        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Students by Grade</h3>
            <div className="flex items-center gap-2">
              <div className="relative">
                <select className="appearance-none pl-3 pr-8 py-2 border border-indigo-200 rounded-[32px] shadow bg-white text-gray-800 text-sm" value={attendanceGranularity} onChange={e => setAttendanceGranularity(e.target.value)}>
                  <option value="day">Last 30 days</option>
                  <option value="month">Last 6 months</option>
                  <option value="year">Last 5 years</option>
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><ChevronDown className="w-4 h-4" /></span>
              </div>
            </div>
          </div>
          {gradeDistribution.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={gradeDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                    {gradeDistribution.map((entry, index) => (<Cell key={index} fill={entry.color} />))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-4">
                {gradeDistribution.map((g, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: g.color }}></div>
                    <span className="text-sm text-gray-600">{g.name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400">No grades configured</div>
          )}
        </div>
      </div>

       {/* New analytics charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Avg Marks by Subject</h3>
          {subjectAvg.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={subjectAvg} margin={{ left: -20 }}>
                <defs>
                  <linearGradient id="gAvg" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="subject_name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" domain={[0,100]} />
                <Tooltip />
                <Line type="monotone" dataKey="avg_marks" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} fill="url(#gAvg)" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400">No data</div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Attendance by Subject</h3>
          {subjectAttendance.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={subjectAttendance} margin={{ left: -20 }}>
                <defs>
                  <linearGradient id="gAtt" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="subject_name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" domain={[0,100]} />
                <Tooltip />
                <Area type="monotone" dataKey="attendance" stroke="#10b981" fill="url(#gAtt)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400">No data</div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Exam Participation</h3>
          {examParticipation.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart cx="50%" cy="50%" outerRadius={80} data={examParticipation}>
                <PolarGrid />
                <PolarAngleAxis dataKey="exam_name" />
                <PolarRadiusAxis angle={30} domain={[0,100]} />
                <Radar name="Participation" dataKey="participation" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.4} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400">No data</div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Top Subjects</h3>
          {topSubjects.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={topSubjects} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="subject_name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" domain={[0,100]} />
                <Tooltip />
                <Bar dataKey="avg_marks" barSize={18} fill="#ec4899" />
                <Line type="monotone" dataKey="avg_marks" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400">No data</div>
          )}
        </div>
      </div>

      {/* Exams × Attendance (duplicate from Dashboard) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mt-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Exams × Attendance</h3>
        {examAttendance.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={examAttendance} margin={{ left: -20, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="exam_label" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" domain={[0, 100]} />
              <Tooltip formatter={(v,name) => [`${v}%`, name]} />
              <Legend />
              <Line type="monotone" dataKey="avg_marks" name="Avg Marks" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="avg_attendance" name="Avg Attendance" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-40 flex items-center justify-center text-gray-400">No exam attendance data</div>
        )}
      </div>

      {/* Re-introduced Top/Least Performing & Recent Students below subject charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Top Performing Students</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Grade</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Avg Marks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topStudents.length > 0 ? topStudents.map((s, i) => (
                  <tr key={i} className="hover:bg-green-50">
                    <td className="px-4 py-2 font-medium text-gray-800">{s.full_name}</td>
                    <td className="px-4 py-2">{s.grade_name}</td>
                    <td className="px-4 py-2">{s.avg_marks}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="3" className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Least Performing Students</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Grade</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Avg Marks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leastStudents.length > 0 ? leastStudents.map((s, i) => (
                  <tr key={i} className="hover:bg-red-50">
                    <td className="px-4 py-2 font-medium text-gray-800">{s.full_name}</td>
                    <td className="px-4 py-2">{s.grade_name}</td>
                    <td className="px-4 py-2">{s.avg_marks}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="3" className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-3 sm:p-4 border-b border-gray-100">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800">Recent Students</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase hidden sm:table-cell">Email</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Grade</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentStudents.length > 0 ? recentStudents.map((s, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium text-xs sm:text-sm text-gray-800 truncate max-w-[120px] sm:max-w-none">{s.full_name}</td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-600 hidden sm:table-cell">{s.email}</td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3"><span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] sm:text-xs">{s.grade_name || '-'}</span></td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3"><span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs ${Number(s.is_active) === 1 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>{Number(s.is_active) === 1 ? 'Active' : 'Inactive'}</span></td>
                </tr>
              )) : (
                <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-400">No students yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
